import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useVirtualizer } from "@tanstack/react-virtual";
import { useCompanyCount, useCompanySearch, useFields } from "../../api/hooks";
import type { Condition, CompanyRow, FilterNode } from "../../api/types";
import { Chip, Empty, FUNCTIONS, Icon, Kpi, PageHead, QualityDot, Segmented, Spinner } from "../../components/ui";
import { errorMessage } from "../../api/client";
import { fmt, siret } from "../../lib/format";
import { useAuth } from "../../lib/auth";
import { FilterEditor, describe, fieldOptions } from "./FilterBuilder";
import { CompanyDrawer } from "./CompanyDrawer";
import { ExportButton } from "./ExportButton";

function useDebounced<T>(v: T, ms: number): T {
  const [d, setD] = useState(v);
  useEffect(() => {
    const t = setTimeout(() => setD(v), ms);
    return () => clearTimeout(t);
  }, [v, ms]);
  return d;
}

function readFilters(sp: URLSearchParams): { mode: "all" | "any"; conds: Condition[] } {
  try {
    const raw = sp.get("f");
    if (!raw) return { mode: "all", conds: [] };
    const node = JSON.parse(raw) as FilterNode;
    return "any" in node ? { mode: "any", conds: node.any } : { mode: "all", conds: node.all };
  } catch {
    return { mode: "all", conds: [] };
  }
}

function issues(c: CompanyRow): number {
  return Object.values(c.quality ?? {}).filter((q) => q.s === "doubtful" || q.s === "invalid").length;
}

export function SearchPage() {
  const me = useAuth();
  const [sp, setSp] = useSearchParams();
  const [q, setQ] = useState(sp.get("q") ?? "");
  const dq = useDebounced(q, 300);
  const { mode, conds } = readFilters(sp);
  const [editing, setEditing] = useState(false);
  const selected = sp.get("c") ? Number(sp.get("c")) : null;
  const fields = useFields();
  const options = useMemo(() => fieldOptions(fields.data ?? []), [fields.data]);
  const searchRef = useRef<HTMLInputElement>(null);

  const filters: FilterNode | null = conds.length ? (mode === "any" ? { any: conds } : { all: conds }) : null;
  const params = { q: dq.trim(), filters };
  const search = useCompanySearch(params);
  const count = useCompanyCount(params);
  const rows = useMemo(() => search.data?.pages.flatMap((p) => p.items) ?? [], [search.data]);

  const update = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(sp);
    Object.entries(patch).forEach(([k, v]) => (v == null || v === "" ? next.delete(k) : next.set(k, v)));
    setSp(next, { replace: true });
  };
  useEffect(() => update({ q: dq.trim() || null }), [dq]); // eslint-disable-line react-hooks/exhaustive-deps
  const setConds = (c: Condition[], m = mode) => update({ f: c.length ? JSON.stringify(m === "any" ? { any: c } : { all: c }) : null });

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  const parentRef = useRef<HTMLDivElement>(null);
  const hasMore = !!search.hasNextPage;
  const v = useVirtualizer({ count: rows.length + (hasMore ? 1 : 0), getScrollElement: () => parentRef.current, estimateSize: () => 60, overscan: 12 });
  const items = v.getVirtualItems();
  const last = items[items.length - 1];
  useEffect(() => {
    if (last && last.index >= rows.length - 1 && hasMore && !search.isFetchingNextPage) search.fetchNextPage();
  }, [last?.index, rows.length, hasMore, search.isFetchingNextPage]); // eslint-disable-line react-hooks/exhaustive-deps

  const c = count.data;
  return (
    <main className="page">
      <PageHead eyebrow="Master Data" title="Recherche & segmentation"
        right={me.can("data.export") && <ExportButton q={params.q} filters={filters} disabled={!c?.companies} />} />

      <div className="row" style={{ alignItems: "flex-start", gap: 12 }}>
        <div className="search">
          <Icon name="search" />
          <input ref={searchRef} className="input" type="search" placeholder="SIRET, SIREN, raison sociale, nom, téléphone, email…" value={q}
            onChange={(e) => setQ(e.target.value)} aria-label="Recherche globale" />
          <kbd aria-hidden="true">⌘K</kbd>
        </div>
      </div>

      <div className="filters" style={{ position: "relative" }}>
        {conds.length > 1 && (
          <Segmented label="Combinaison" value={mode} onChange={(m) => setConds(conds, m)}
            options={[["all", "Toutes les conditions"], ["any", "Au moins une"]]} />
        )}
        {conds.map((cd, i) => (
          <span key={i} className="fchip">{describe(cd, options)}
            <button type="button" aria-label={`Retirer le filtre ${describe(cd, options)}`} onClick={() => setConds(conds.filter((_, j) => j !== i))}><Icon name="x" size={12} /></button>
          </span>
        ))}
        <div style={{ position: "relative" }}>
          <button className="btn sm" type="button" onClick={() => setEditing((x) => !x)} aria-expanded={editing} disabled={!options.length}><Icon name="filter" />Ajouter un filtre</button>
          {editing && <FilterEditor options={options} onClose={() => setEditing(false)} onAdd={(cd) => { setConds([...conds, cd]); setEditing(false); }} />}
        </div>
        {conds.length > 0 && <button className="btn sm ghost" type="button" onClick={() => setConds([])}>Tout effacer</button>}
      </div>

      <div className="kpis">
        <Kpi label="Entreprises" value={count.isLoading ? "…" : <span className="grad">{fmt(c?.companies)}</span>} sub={filters || params.q ? "correspondant à la sélection" : "dans la Master Data"} focus />
        <Kpi label="Contacts" value={count.isLoading ? "…" : fmt(c?.contacts)} sub="rattachés à ces entreprises" />
        <Kpi label="Contacts par entreprise" value={c && c.companies ? fmt(c.contacts / c.companies, 1) : "–"} sub="en moyenne" />
      </div>
      {count.error && <div className="banner bad" role="alert">{errorMessage(count.error)}</div>}

      <div className="tile flush">
        <div className="vlist" ref={parentRef} role="grid" aria-label="Entreprises" aria-rowcount={c?.companies ?? rows.length}>
          <div className="vhead" role="row">
            <span role="columnheader">Entreprise</span><span role="columnheader">Localisation</span><span role="columnheader">Activité</span>
            <span role="columnheader">Dirigeant principal</span><span role="columnheader">Téléphone</span><span role="columnheader">Qualité</span>
          </div>
          {search.isLoading && <div className="empty"><Spinner /></div>}
          {!search.isLoading && rows.length === 0 && (
            <Empty title="Aucune entreprise">{filters || params.q ? "Élargissez la recherche ou retirez un filtre." : "Importez un premier fichier dans l'onglet Imports."}</Empty>
          )}
          <div style={{ height: v.getTotalSize(), position: "relative" }}>
            {items.map((it) => {
              const r = rows[it.index];
              if (!r) return <div key="more" className="vrow" style={{ top: it.start, cursor: "default" }}><Spinner /></div>;
              const n = issues(r);
              return (
                <div key={r.id} className="vrow" role="row" tabIndex={0} aria-selected={selected === r.id} style={{ top: it.start }}
                  onClick={() => update({ c: String(r.id) })} onKeyDown={(e) => e.key === "Enter" && update({ c: String(r.id) })}>
                  <div className="cell"><b title={r.name ?? ""}>{r.name ?? "Sans raison sociale"}</b><span className="mono">{siret(r.siret)}</span></div>
                  <div className="cell"><b>{r.city ?? "–"}</b><span>{[r.postal_code, r.department && `dép. ${r.department}`].filter(Boolean).join(" · ") || "–"}</span></div>
                  <div className="cell hide-sm"><b title={r.naf_label ?? ""}>{r.naf_label ?? "–"}</b><span>{[r.naf_code, r.headcount_band].filter(Boolean).join(" · ") || "–"}</span></div>
                  <div className="cell hide-sm"><b>{r.primary_contact?.name ?? "–"}</b><span>{r.primary_contact ? (FUNCTIONS[r.primary_contact.function_code ?? ""] ?? r.primary_contact.job_title ?? "") : ""}{r.contacts ? ` · ${r.contacts} contact${r.contacts > 1 ? "s" : ""}` : ""}</span></div>
                  <div className="cell hide-sm">{r.phone ? <b className="row" style={{ gap: 6 }}><QualityDot q={r.phone.quality} />{r.phone.value}</b> : <span>–</span>}</div>
                  <div className="cell">{n ? <Chip tone="orange" dot>{n} à vérifier</Chip> : <Chip tone="green" dot>OK</Chip>}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      {search.error && <div className="banner bad" role="alert">{errorMessage(search.error)}</div>}
      <CompanyDrawer id={selected} onClose={() => update({ c: null })} fields={fields.data ?? []} />
    </main>
  );
}
